import { MapMouseEvent } from 'mapbox-gl';
import { useEffect } from 'react';
import { Layer, LayerProps, Source, useMap } from 'react-map-gl/mapbox';
import { useTheme } from 'styled-components';

import { SHAPE_FILL_OPACITY } from '../../../constants';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { ZONE_COLOR, ZONE_FILL_COLOR } from '../../../MultiFlightMap.helpers';
import { useMapPopover } from '../../MapPopover/MapPopover.context';
import { fitBoundsOptions } from '../../MapView/map.helpers';
import { NewLocationConfirmation } from '../../NewLocationConfirmation/NewLocationConfirmation';

import type { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';

type Props = {
  cartographyLevel: string;
  country: string;
  locations: Partial<Zone>[];
  mapId: string;
};

export function CartographyLevelLayer({ cartographyLevel, country, locations, mapId }: Props) {
  const layerName = 'layer';
  const { [mapId]: map } = useMap();
  const { clearTemporaryLocation, confirmAddLocation, selectedItemId, setList, setSelectedItemId, temporaryLocation } =
    useMultiFlightMap();
  const { closeMapPopover, openMapPopover } = useMapPopover();
  const { uiColors }: any = useTheme();

  const sourceId = `cartography_${country.toLowerCase()}_${cartographyLevel}`;

  const tiles = [`${taptap.config.mbtilesServer}${sourceId}/{z}/{x}/{y}.pbf`];

  const sourceProps = {
    id: sourceId,
    key: sourceId,
    maxzoom: 14,
    minzoom: 0,
    tiles
  };

  const cartographyLevelIds = locations.map(loc => loc.cartographyLevelId);
  const smartIds = locations.filter(loc => loc.smart).map(loc => loc.cartographyLevelId);
  const visibleIds = locations.filter(loc => loc.visible).map(loc => loc.cartographyLevelId);
  const selectedLocation = locations.filter(loc => loc._id === selectedItemId);
  const selectedZoneId = selectedLocation.length > 0 ? selectedLocation[0].cartographyLevelId : null;

  const invisibleIds = locations.filter(loc => !loc.visible).map(loc => loc.cartographyLevelId);

  const zoneFillBranches: Array<[unknown, string, number]> = [
    [['==', ['get', 'name'], selectedZoneId || ''], uiColors.selectedZoneColor, SHAPE_FILL_OPACITY],
    [['in', ['get', 'name'], ['literal', smartIds]], uiColors.smartZoneColor, SHAPE_FILL_OPACITY],
    [
      ['==', ['get', 'name'], temporaryLocation?.cartographyLevelId || ''],
      uiColors.unconfirmedZoneColor,
      SHAPE_FILL_OPACITY
    ],
    [['in', ['get', 'name'], ['literal', visibleIds]], ZONE_FILL_COLOR, 1],
    [['in', ['get', 'name'], ['literal', invisibleIds]], uiColors.unselectedZoneColor, SHAPE_FILL_OPACITY]
  ];

  const fillLayerOptions: LayerProps = {
    filter: ['in', 'name', ...cartographyLevelIds],
    id: `location_layer_${sourceId}`,
    paint: {
      'fill-color': ['case', ...zoneFillBranches.flatMap(([condition, color]) => [condition, color]), ZONE_FILL_COLOR],
      'fill-opacity': ['case', ...zoneFillBranches.flatMap(([condition, , opacity]) => [condition, opacity]), 1]
    },
    source: sourceId,
    'source-layer': layerName,
    type: 'fill'
  };

  const outlineLayerOptions: LayerProps = {
    filter: ['in', 'name', ...cartographyLevelIds],
    id: `location_layer_outline_${sourceId}`,
    paint: {
      'line-color': [
        'case',
        ['==', ['get', 'name'], selectedZoneId || ''],
        uiColors.selectedZoneColor,
        ['in', ['get', 'name'], ['literal', smartIds]],
        uiColors.smartZoneColor,
        ['==', ['get', 'name'], temporaryLocation?.cartographyLevelId || ''],
        uiColors.unconfirmedZoneColor,
        ['in', ['get', 'name'], ['literal', visibleIds]],
        ZONE_COLOR,
        ['in', ['get', 'name'], ['literal', invisibleIds]],
        uiColors.unselectedZoneColor,
        ZONE_COLOR
      ],
      'line-opacity': 1,
      'line-width': 2
    },
    source: sourceId,
    'source-layer': layerName,
    type: 'line'
  };

  useEffect(() => {
    if (!map) {
      return;
    }

    const checkSourceReady = () => {
      if (map.getSource(sourceId) && map.isSourceLoaded(sourceId)) {
        map.off('render', checkSourceReady);
      }
    };

    map.on('render', checkSourceReady);
    return () => {
      map.off('render', checkSourceReady);
    };
  }, [map, sourceId]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const run = async () => {
      if (temporaryLocation?.boundingBox) {
        map.fitBounds(temporaryLocation?.boundingBox, {
          ...fitBoundsOptions,
          padding: {
            bottom: 120,
            left: 80,
            right: 80,
            top: 80
          }
        });

        map.once('zoomend', () => {
          const centerLngLat = map.getCenter();
          const centerPoint = map.project(centerLngLat);
          const rect = map.getContainer().getBoundingClientRect();

          openMapPopover({
            render: () => (
              <NewLocationConfirmation
                onAddClick={() => {
                  closeMapPopover();
                  confirmAddLocation();
                }}
                onCancelClick={() => {
                  closeMapPopover();
                  clearTemporaryLocation();
                }}
              />
            ),
            x: centerPoint.x + rect.left,
            y: centerPoint.y + rect.top
          });
        });
      }
    };

    run();
  }, [map, temporaryLocation]);

  useEffect(() => {
    const mapInstance = map?.getMap();

    if (!mapInstance || !fillLayerOptions.id) {
      return;
    }

    const handleClick = (e: MapMouseEvent) => {
      const feature = e.features?.[0];
      const cartographyLevelId = feature?.properties?.name;
      setList('locations');

      if (cartographyLevelId) {
        const selectedZone = locations.find(loc => loc.cartographyLevelId === cartographyLevelId);

        if (selectedZone && selectedZone._id) {
          setSelectedItemId(selectedZone._id.toString());
        }
      }
    };

    mapInstance.on('click', fillLayerOptions.id, handleClick);

    return () => {
      if (fillLayerOptions.id) {
        mapInstance.off('click', fillLayerOptions.id, handleClick);
      }
    };
  }, [map, setSelectedItemId, fillLayerOptions.id, locations]);

  return (
    <Source {...sourceProps} type="vector">
      <Layer {...fillLayerOptions} />
      <Layer {...outlineLayerOptions} />
    </Source>
  );
}
